import React, { useState } from "react";
import {
  Stack,
  Text,
  TextLink,
  Heading,
  Column,
  Columns,
  TextField,
  Divider,
  Checkbox,
} from "@origin-digital/ods-core";
import { Field, FieldMetaProps, FieldProps, FormikProps } from "formik";
import { FormValues, HandoverTypeEnum } from "../../types";
import FormFieldLayout from "../../FormComponents/FormFieldLayout";
import { useCloseTransferFormProps } from "./useCloseTransferFormProps";

const HyperLinkTextForAccountHolderDetails: React.FC = () => (
  <Text variant="body-small" tone="neutralLight">
    The account holder will be sent a link to{" "}
    <TextLink
      href="https://www.originenergy.com.au/electricity-gas/plans.html"
      target="_blank"
    >
      compare our energy plans
    </TextLink>
    .
  </Text>
);

const EditableAccountHolderDetails: React.FC = () => {
  return (
    <Stack
      space={{
        xs: "large",
        md: "xlarge",
      }}
    >
      {/* First + last name */}
      <Columns
        collapseBelow="md"
        space={{
          xs: "xlarge",
          md: "medium",
        }}
      >
        <Column>
          <Field name="closeTransfer.accountHolder.firstName">
            {({
              field,
              meta,
            }: {
              field: FormikProps<FieldProps>;
              meta: FieldMetaProps<FormValues>;
            }) => (
              <TextField
                {...field}
                id="firstName"
                variant="outlined"
                label="First name"
                hideHelperTextSpace={true}
                error={!!meta.error}
                helperText={meta.error}
              />
            )}
          </Field>
        </Column>
        <Column>
          <Field name="closeTransfer.accountHolder.lastName">
            {({
              field,
              meta,
            }: {
              field: FormikProps<FieldProps>;
              meta: FieldMetaProps<FormValues>;
            }) => (
              <TextField
                {...field}
                id="lastName"
                variant="outlined"
                label="Last name"
                hideHelperTextSpace={true}
                error={!!meta.error}
                helperText={meta.error}
              />
            )}
          </Field>
        </Column>
      </Columns>
      {/* End First + last name */}

      {/* Email */}
      <Field name="closeTransfer.accountHolder.email">
        {({
          field,
          meta,
        }: {
          field: FormikProps<FieldProps>;
          meta: FieldMetaProps<FormValues>;
        }) => (
          <TextField
            {...field}
            id="email"
            variant="outlined"
            label="Email"
            hideHelperTextSpace={true}
            error={!!meta.error}
            helperText={meta.error}
          />
        )}
      </Field>
      {/* End Email */}
    </Stack>
  );
};

export const ReadonlyAccountHolderDetails: React.FC = () => {
  const {
    formikContext: {
      values: {
        closeTransfer: { accountHolder },
      },
    },
  } = useCloseTransferFormProps();
  return (
    <Stack
      space={{
        xs: "large",
        md: "xlarge",
      }}
    >
      {/* First + last name */}
      <Columns
        collapseBelow="md"
        space={{
          xs: "xlarge",
          md: "medium",
        }}
      >
        <Column>
          <Stack space="xsmall">
            <Stack space="xxsmall">
              <Text variant="body-small" tone="neutralLight">
                First name
              </Text>
              <Text>{accountHolder?.firstName}</Text>
            </Stack>
            <Divider />
          </Stack>
        </Column>
        <Column>
          <Stack space="xsmall">
            <Stack space="xxsmall">
              <Text variant="body-small" tone="neutralLight">
                Last name
              </Text>
              <Text>{accountHolder?.lastName}</Text>
            </Stack>
            <Divider />
          </Stack>
        </Column>
      </Columns>
      {/* End First + last name */}

      {/* Email */}
      <Stack space="xsmall">
        <Stack space="xxsmall">
          <Text variant="body-small" tone="neutralLight">
            Email
          </Text>
          <Text>{accountHolder?.email}</Text>
        </Stack>
        <Divider />
      </Stack>
      {/* End Email */}
    </Stack>
  );
};

export const CloseTransferAccountSection = () => {
  const {
    formikContext: { values, setFieldValue },
    formBehavior: { isElectricityReadOnly, isGasReadOnly },
  } = useCloseTransferFormProps();

  const { closeTransfer } = values;
  const [checkedExistingUser, setCheckedExistingUser] =
    useState<boolean>(false);
  const [defaultVal, setDefaultVal] = useState<boolean>(false);
  const isExistingAccountHolderDetailsAvailable = React.useMemo(() => {
    const {
      accountPrimaryFirstName,
      accountPrimaryLastName,
      accountPrimaryEmail,
    } = values;
    const acctHolderExists =
      !!accountPrimaryFirstName?.trim() &&
      !!accountPrimaryLastName?.trim() &&
      !!accountPrimaryEmail?.trim();
    setDefaultVal(acctHolderExists);
    return acctHolderExists;
  }, [
    values.accountPrimaryFirstName,
    values.accountPrimaryLastName,
    values.accountPrimaryEmail,
  ]);

  React.useEffect(() => {
    if (
      defaultVal &&
      closeTransfer.handoverType === HandoverTypeEnum.CLOSE_AND_TRANSFER
    ) {
      setFieldValue("closeTransfer.accountHolder", {
        firstName: values.accountPrimaryFirstName,
        lastName: values.accountPrimaryLastName,
        email: values.accountPrimaryEmail,
      });
      setCheckedExistingUser(true);
    } else {
      setCheckedExistingUser(false);
    }
  }, [defaultVal, closeTransfer.handoverType]);

  const handleUseExistingAccountHolderDetails = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (
        event.target.checked ||
        defaultVal
        // values.closeTransfer.accountHolder === undefined
      ) {
        // Populate the form with existing account holder details
        setFieldValue("closeTransfer.accountHolder", {
          firstName: values.accountPrimaryFirstName,
          lastName: values.accountPrimaryLastName,
          email: values.accountPrimaryEmail,
        });
      } else if (
        !event.target.checked &&
        values.closeTransfer.accountHolder !== undefined
      ) {
        setFieldValue("closeTransfer.accountHolder", undefined);
      }
      setDefaultVal(!defaultVal);
    },
    [values, setFieldValue]
  );
  const isAccountReadonly = isElectricityReadOnly || isGasReadOnly;
  return (
    <>
      {closeTransfer.handoverType === HandoverTypeEnum.CLOSE_AND_TRANSFER &&
        !isAccountReadonly && (
          <Stack
            space={{
              xs: "large",
              md: "xlarge",
            }}
          >
            {/* <Heading variant="h4">Account holder details</Heading> */}
            <Stack space="large">
              <Heading variant="h4">Account holder details</Heading>
              {isExistingAccountHolderDetailsAvailable && (
                <Checkbox
                  label="Use existing account holder details"
                  id="use-existing-account-holder-details"
                  onChange={handleUseExistingAccountHolderDetails}
                  checked={defaultVal}
                />
              )}
            </Stack>
            <FormFieldLayout
              multipleFields
              text={<HyperLinkTextForAccountHolderDetails />}
            >
              {/* Fields */}
              {checkedExistingUser ? (
                <ReadonlyAccountHolderDetails />
              ) : (
                <EditableAccountHolderDetails />
              )}
            </FormFieldLayout>
          </Stack>
        )}
      {closeTransfer.handoverType === HandoverTypeEnum.CLOSE_AND_TRANSFER &&
        isAccountReadonly && (
          <Stack space="large">
            <Heading variant="h4">Account holder</Heading>
            <ReadonlyAccountHolderDetails />
          </Stack>
        )}
    </>
  );
};
